import * as XLSX from 'xlsx';

// Format time value from Excel
export const formatTimeValue = (val) => {
  if (val === null || val === undefined || val === '') return '';
  
  // If it's a decimal (Excel time format) or zero
  if (typeof val === 'number') {
    const totalMinutes = Math.round(val * 24 * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }
  
  // If it's already a string time
  const strVal = String(val).trim();
  if (strVal.includes(':')) return strVal;
  
  return strVal;
};

// Parse Excel sheet (for Mover and other customers)
export const parseExcelSheet = (workbook, sheetName, primaryCustomer) => {
  try {
    const sheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    
    if (jsonData.length < 2) {
      return { data: [], error: null };
    }
    
    // Find header row - look for a row containing key column names
    // Only check first 15 columns to avoid interference from side tables (e.g., Active Routes in Bpost)
    let headerRowIndex = -1;
    const headerKeywords = ['datum', 'date', 'tarih', 'chauffeur', 'driver', 'şoför', 'plaka', 'plate', 'nrplaat', 'kenteken', 'nummerplaat', 'rit'];
    const primaryKeywords = ['datum', 'date', 'tarih']; // At least one of these must match
    
    for (let i = 0; i < Math.min(20, jsonData.length); i++) {
      // Only check first 15 columns to ignore side-tables (Active Routes in columns S-Z)
      const rowCells = jsonData[i].slice(0, 15);
      const rowText = rowCells.map(c => String(c || '').toLowerCase()).join(' ');
      const matchCount = headerKeywords.filter(kw => rowText.includes(kw)).length;
      const hasPrimaryKeyword = primaryKeywords.some(kw => rowText.includes(kw));
      
      // Require at least 2 keyword matches AND one primary keyword (datum/date/tarih)
      if (matchCount >= 2 && hasPrimaryKeyword) {
        headerRowIndex = i;
        break;
      }
    }
    
    if (headerRowIndex === -1) {
      headerRowIndex = 0;
    }
    
    // Only use first 15 headers (columns A-O) to avoid side-table columns
    const allHeaders = jsonData[headerRowIndex].map(h => String(h || '').toLowerCase().trim());
    const headers = allHeaders.slice(0, Math.min(allHeaders.length, 
      // Find the last meaningful Bpost/route column - stop at column 15 or at the end of known headers
      Math.max(15, allHeaders.findIndex((h, idx) => idx > 10 && h === '') || allHeaders.length)
    ));
    const data = [];
    
    // Debug: log headers
    console.log('Excel headers found:', headers);
    
    for (let i = headerRowIndex + 1; i < jsonData.length; i++) {
      const rowValues = jsonData[i];
      
      const hasData = rowValues.some(v => v !== '' && v !== null && v !== undefined);
      if (!hasData) continue;
      
      const rowTextLower = rowValues.map(v => String(v || '').toLowerCase()).join(' ');
      const skipKeywords = ['totaal', 'total', 'toplam', 'subtotal', 'grand total'];
      const isSummaryRow = skipKeywords.some(kw => rowTextLower.includes(kw));
      if (isSummaryRow) continue;
      
      const row = {};
      
      headers.forEach((h, idx) => {
        const val = rowValues[idx];
        const strVal = val !== undefined && val !== null ? String(val).trim() : '';
        
        // Date column
        if (h.includes('datum') || h.includes('date') || h.includes('tarih')) {
          if (typeof val === 'number') {
            const date = XLSX.SSF.parse_date_code(val);
            row.date = `${date.y}-${String(date.m).padStart(2,'0')}-${String(date.d).padStart(2,'0')}`;
          } else if (val instanceof Date) {
            row.date = val.toISOString().split('T')[0];
          } else if (strVal) {
            row.date = strVal;
          }
        }
        // Route number - FedEx: route_number, GeTax/Express: route_type, Bpost: route_number
        else if ((h.includes('route') && (h.includes('no') || h.includes('nummer'))) || h === 'route nummer') {
          if (primaryCustomer === 'fedex' || primaryCustomer === 'bpost' || primaryCustomer === 'dhl') {
            row.route_number = strVal;
          } else if (primaryCustomer === 'getax' || primaryCustomer === 'express') {
            row.route_type = strVal;
          }
        }
        // Rit column - for Mover this is the location (Luik, Hasselt, etc.)
        else if (h === 'rit' || h === 'rit nr' || h === 'plaats') {
          if (strVal) {
            row.plaats = strVal;
          }
        }
        // Rit Number (separate field)
        else if (h.includes('rit') && (h.includes('nr') || h.includes('no') || h.includes('nummer'))) {
          row.rit_number = strVal;
        }
        // Plaats/Yer (location)
        else if (h.includes('plaats') || h.includes('yer') || h.includes('location')) {
          if (strVal && !row.plaats) {
            row.plaats = strVal;
          }
        }
        // Opmerkingen as plaats fallback
        else if (h === 'opmerkingen' || h.includes('opmerking')) {
          if (strVal && !row.plaats) {
            row.plaats = strVal;
          }
        }
        // Driver name
        else if (h.includes('chauffeur') || h.includes('şoför') || h.includes('driver') || h.includes('naam chauffeur')) {
          row.driver_name = strVal;
        }
        // Copilot name
        else if (h.includes('copilot') || h.includes('yardımcı') || h.includes('co-pilot') || h.includes('naam copilot')) {
          row.copilot_name = strVal;
        }
        // License plate - IMPROVED matching for Mover
        else if (
          h === 'nrplaat' || 
          h === 'nummerplaat' || 
          h === 'nr plaat' || 
          h === 'plaat' ||
          h === 'kenteken' ||
          h.includes('plaka') || 
          h.includes('plate') || 
          h.includes('kenteken') || 
          h.includes('nrplaat') || 
          h.includes('nummerplaat') ||
          h.includes('nr plaat')
        ) {
          if (strVal) {
            row.license_plate = strVal.toUpperCase().replace(/\s+/g, '-');
          }
        }
        // Hourly rate (Uurprijs)
        else if (h.includes('uurprijs') || (h.includes('saat') && h.includes('ücret'))) {
          const numVal = strVal.replace('€', '').replace(',', '.').trim();
          row.hourly_rate = numVal || (primaryCustomer === 'mover' ? '78' : (primaryCustomer === 'getax' || primaryCustomer === 'express') ? '325' : (primaryCustomer === 'bpost' || primaryCustomer === 'dhl') ? '0' : '40');
        }
        // Start cost - handle double space "Start  kost"
        else if ((h.includes('start') && h.includes('kost')) || h === 'start  kost') {
          const numericVal = typeof val === 'number' ? val : parseFloat(strVal.replace('€', '').replace(',', '.').trim()) || 0;
          row.start_cost = numericVal.toString();
        }
        // Stop prijs (DHL)
        else if (h.includes('stop') && h.includes('prijs')) {
          const numericVal = typeof val === 'number' ? val : parseFloat(strVal.replace('€', '').replace(',', '.').trim()) || 0;
          row.start_cost = numericVal.toString();
        }
        // Stops count (DHL)
        else if (h === 'stops') {
          row.stops = parseInt(strVal) || 0;
        }
        // Werkuur Start (Bpost) - ignored for calculation, just reference
        else if (h === 'werkuur start' || h === 'werkuur') {
          row.werkuur = formatTimeValue(val);
        }
        // Aantal Uren (Bpost) - work hours, can be pre-calculated in Excel
        else if (h === 'aantal uren' || h === 'aantal_uren') {
          if (typeof val === 'number') {
            row.aantal_uren = val;
          } else {
            row.aantal_uren = parseFloat(strVal.replace(',', '.')) || 0;
          }
        }
        // Kost Werktijd (Bpost) - work cost reference
        else if (h === 'kost werktijd' || h.includes('kost werktijd')) {
          const numericVal = typeof val === 'number' ? val : parseFloat(strVal.replace('€', '').replace(',', '.').trim()) || 0;
          row.work_cost = numericVal.toString();
        }
        // Totaal excl BTW (Bpost) - total cost = start_cost
        else if (h.includes('totaal') && h.includes('btw')) {
          const numericVal = typeof val === 'number' ? val : parseFloat(strVal.replace('€', '').replace(',', '.').trim()) || 0;
          row.totaal_excl_btw = numericVal.toString();
        }
        // Begin Km
        else if (h === 'begin km' || (h.includes('begin') && h.includes('km'))) {
          const kmVal = parseInt(strVal) || 0;
          if (!row.km_start) row.km_start = kmVal.toString();
        }
        // Eind KM
        else if (h === 'eind km' || (h.includes('eind') && h.includes('km'))) {
          const kmVal = parseInt(strVal) || 0;
          if (!row.km_end) row.km_end = kmVal.toString();
        }
        // Totaal Km
        else if (h === 'totaal km' || (h.includes('totaal') && h.includes('km') && !h.includes('btw'))) {
          // We calculate this from begin/end, but store if explicitly provided
          row.km_total = parseInt(strVal) || 0;
        }
        // Start time - handle headers like "startuur 00:00"
        else if (h.includes('startuur') || h === 'start uur' || (h.includes('start') && h.includes('uur') && !h.includes('kost'))) {
          row.start_time = formatTimeValue(val);
        }
        // End time - handle headers like "einduur 00:00"
        else if (h.includes('einduur') || h === 'eind uur' || (h.includes('eind') && h.includes('uur'))) {
          row.end_time = formatTimeValue(val);
        }
        // Break time
        else if (h.includes('pauze') || h.includes('mola') || h.includes('break')) {
          row.break_time = formatTimeValue(val);
        }
        // KM Start
        else if (h.includes('km') && h.includes('start')) {
          const kmVal = parseInt(strVal) || 0;
          row.km_start = kmVal.toString();
        }
        // KM End
        else if (h.includes('km') && (h.includes('end') || h.includes('eind'))) {
          const kmVal = parseInt(strVal) || 0;
          row.km_end = kmVal.toString();
        }
        // Route type (GeTax/Express)
        else if (h.includes('tur') && h.includes('tip')) {
          row.route_type = strVal;
        }
        // Notes
        else if (h.includes('opmerking') || h.includes('not') || h.includes('notes')) {
          row.notes = strVal;
        }
      });
      
      const isValidDate = row.date && !isNaN(new Date(row.date).getTime());
      
      if (isValidDate && (row.driver_name || row.license_plate || row.rit_number || row.plaats || row.route_number)) {
        if (!row.start_time) row.start_time = '08:00';
        if (!row.end_time) row.end_time = '17:00';
        if (!row.break_time) row.break_time = primaryCustomer === 'mover' ? '00:30' : '00:00';
        
        // For Bpost: if totaal_excl_btw is provided and start_cost is not, use it
        if (primaryCustomer === 'bpost') {
          if (row.totaal_excl_btw && row.totaal_excl_btw !== '0' && (!row.start_cost || row.start_cost === '0')) {
            row.start_cost = row.totaal_excl_btw;
          }
          // Also try work_cost (Kost Werktijd) as fallback
          if (row.work_cost && row.work_cost !== '0' && (!row.start_cost || row.start_cost === '0')) {
            row.start_cost = row.work_cost;
          }
          // Set hourly_rate to 0 for Bpost
          row.hourly_rate = '0';
        }
        
        // DHL: set hourly_rate to 0 (not used for calculation)
        if (primaryCustomer === 'dhl') {
          row.hourly_rate = '0';
        }
        
        data.push(row);
      }
    }
    
    return { data, error: null };
  } catch (err) {
    console.error('Sheet parse error:', err);
    return { data: [], error: err.message };
  }
};

// Parse CSV file
export const parseCSVFile = (text) => {
  try {
    const lines = text.split('\n').filter(line => line.trim());
    const headers = lines[0].split(/[;,\t]/).map(h => h.trim().toLowerCase());
    
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(/[;,\t]/);
      const row = {};
      
      headers.forEach((h, idx) => {
        const val = values[idx]?.trim() || '';
        if (h.includes('tarih') || h.includes('date') || h.includes('datum')) row.date = val;
        else if (h.includes('plaka') || h.includes('plate')) row.license_plate = val;
        else if (h.includes('şoför') || h.includes('driver') || h.includes('sofor') || h.includes('chauffeur')) row.driver_name = val;
        else if (h.includes('copilot') || h.includes('yardımcı')) row.copilot_name = val;
        else if (h.includes('route') && h.includes('no')) row.route_number = val;
        else if (h.includes('rit')) row.rit_number = val;
        else if (h.includes('plaats') || h.includes('yer')) row.plaats = val;
        else if (h.includes('tur') && h.includes('tip')) row.route_type = val;
        else if (h.includes('km') && h.includes('baş')) row.km_start = val;
        else if (h.includes('km') && h.includes('bit')) row.km_end = val;
        else if ((h.includes('başlangıç') || h.includes('start')) && (h.includes('saat') || h.includes('uur'))) row.start_time = val;
        else if ((h.includes('bitiş') || h.includes('eind')) && (h.includes('saat') || h.includes('uur'))) row.end_time = val;
        else if (h.includes('mola') || h.includes('pauze')) row.break_time = val;
        else if ((h.includes('saat') || h.includes('uur')) && (h.includes('ücret') || h.includes('prijs'))) row.hourly_rate = val;
        else if (h.includes('start') && h.includes('kost')) row.start_cost = val;
      });
      
      if (row.date || row.license_plate || row.driver_name) {
        data.push(row);
      }
    }
    
    return { data, error: null };
  } catch (err) {
    console.error('CSV parse error:', err);
    return { data: [], error: err.message };
  }
};

// Read Excel file and return workbook info
export const readExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheets = workbook.SheetNames;
        resolve({ workbook, sheets });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('File read error'));
    reader.readAsArrayBuffer(file);
  });
};

// Read CSV file
export const readCSVFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        resolve(parseCSVFile(text));
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('File read error'));
    reader.readAsText(file);
  });
};
